import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

// Layout: a two-column grid — icon, then content — rather than an absolutely
// positioned icon. The icon shares row 1 with the first content child and is
// nudged 2px so it optically centres on that line, whether the alert starts
// with an AlertTitle or a single-line AlertDescription. `col-start-2` is
// applied from here (not inside AlertTitle/AlertDescription) so that arbitrary
// children land in the content column too. Without an icon the alert collapses
// to a single full-width column — a second, zero-width column would strand a
// bare text child (which has no element to hang `col-start-2` on) inside it.
const alertVariants = cva(
  "relative grid w-full grid-cols-1 items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[1rem_1fr] has-[>svg]:gap-x-3 has-[>svg]:[&>*:not(svg)]:col-start-2 [&>svg]:size-4 [&>svg]:translate-y-[2px] [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
        // Soft status callouts (DESIGN.md › Colors › Soft status surfaces): tint and
        // border derive from the status token (/10 and /40); body text stays
        // `foreground` for contrast — only the icon carries the status color.
        warning: "border-warning/40 bg-warning/10 [&>svg]:text-warning",
        info: "border-info/40 bg-info/10 [&>svg]:text-info",
        success: "border-success/40 bg-success/10 [&>svg]:text-success",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn("min-h-4 font-medium leading-none tracking-tight", className)}
      {...props}
    />
  ),
);
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("text-sm [&_p]:leading-relaxed", className)} {...props} />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertDescription, AlertTitle };
